import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiArrowRight, FiBriefcase, FiX, FiCheck } from 'react-icons/fi';
import { IoSparkles } from 'react-icons/io5';
import JobItem from './jobItem';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RecruiterApi } from '../../../supports/recruiterApi';
import Loader from "../../../common/loader";
import unifiedAuthManager from '../../../services/unifiedAuthManager';
import { ACTIVE_STATUSES, generateRequestId } from '../../../common/common';

const PAGE_SIZE = 10;

// SVG Filter Icon as a React component
const FilterIcon = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M21 6H19M21 12H16M21 18H16M7 20V13.5612C7 13.3532 7 13.2492 6.97958 13.1497C6.96147 13.0615 6.93151 12.9761 6.89052 12.8958C6.84431 12.8054 6.77934 12.7242 6.64939 12.5617L3.35061 8.43826C3.22066 8.27583 3.15569 8.19461 3.10948 8.10417C3.06849 8.02393 3.03853 7.93852 3.02042 7.85026C3 7.75078 3 7.64677 3 7.43875V5.6C3 5.03995 3 4.75992 3.10899 4.54601C3.20487 4.35785 3.35785 4.20487 3.54601 4.10899C3.75992 4 4.03995 4 4.6 4H13.4C13.9601 4 14.2401 4 14.454 4.10899C14.6422 4.20487 14.7951 4.35785 14.891 4.54601C15 4.75992 15 5.03995 15 5.6V7.43875C15 7.64677 15 7.75078 14.9796 7.85026C14.9615 7.93852 14.9315 8.02393 14.8905 8.10417C14.8443 8.19461 14.7793 8.27583 14.6494 8.43826L11.3506 12.5617C11.2207 12.7242 11.1557 12.8054 11.1095 12.8958C11.0685 12.9761 11.0385 13.0615 11.0204 13.1497C11 13.2492 11 13.3532 11 13.5612V17L7 20Z"
      stroke="#004645"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const JobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [searchTotalCount, setSearchTotalCount] = useState(null);
  const hasFetched = useRef(false);
  const scrollContainerRef = useRef(null);
  const location = useLocation();
  const query = useQuery();
  const jobsListContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const navEntries = performance.getEntriesByType('navigation');
    const isReload = navEntries[0]?.type === 'reload';
    if (isReload && location.search) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, navigate]);

  const getRecruiterId = () => {
    const user = unifiedAuthManager.getUnifiedUser();
    return user?.id;
  };

  const fetchDefaultJobs = useCallback(
    async (statusArray = selectedStatus) => {
      setLoading(true);
      setError(null);
      setSearchTotalCount(null); // Reset the total count if switching to normal mode
      try {
        const recruiterId = getRecruiterId();
        if (!recruiterId) {
          setJobs([]);
          setError('Recruiter ID not found in user info');
          setLoading(false);
          return;
        }
        const statusesToFetch = statusArray.length === 0 ? ACTIVE_STATUSES : statusArray;
        const requestBody = {
          recruiter_id: recruiterId,
          user_payload: {
            request_id: generateRequestId()
          },
          status: statusesToFetch
        };
        const response = await RecruiterApi.getJobsListOnStatus(requestBody);
        if (response.success) {
          setJobs(response.data || []);
        } else {
          setJobs([]);
          setError(response.message);
        }
      } catch (err) {
        setJobs([]);
        setError('Failed to fetch jobs');
      } finally {
        setLoading(false);
      }
    }, [selectedStatus]
  );

  const doSearch = useCallback(async (searchText, pageNum = 0, append = false) => {
    if (!searchText) return;
    setLoading(pageNum === 0);
    setIsFetchingMore(pageNum > 0);
    setError(null);
    if (pageNum === 0) setPage(0);
    if (pageNum === 0) setHasMore(true);
    try {
      const recruiterId = getRecruiterId();
      if (!recruiterId) {
        setJobs([]);
        setError('Recruiter ID not found in user info');
        setLoading(false);
        setIsFetchingMore(false);
        setSearchTotalCount(null); // Reset on error
        return;
      }
      const requestBody = {
        user_payload: {
          request_id: generateRequestId()
        },
        count: PAGE_SIZE,
        page: pageNum,
        search_text: searchText,
        recruiter_id: recruiterId
      };
      const response = await RecruiterApi.getJobsBySearch(requestBody);
      if (response.success && Array.isArray(response.data?.job_descriptions)) {
        const newMetadata = response.data.job_descriptions;
        const total = typeof response.data.total_count === "number" ? response.data.total_count : null;
        setSearchTotalCount(total);
        if (append) {
          setJobs(prev => [...prev, ...newMetadata]);
        } else {
          setJobs(newMetadata);
        }
        setHasMore(total > newMetadata.length);
        if (pageNum === 0) setPage(0);
        else setPage(pageNum);
      } else {
        if (!append) setJobs([]);
        setHasMore(false);
        setSearchTotalCount(null);
        setError('No jobs found.');
      }
    } catch (err) {
      if (!append) setJobs([]);
      setHasMore(false);
      setSearchTotalCount(null);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const urlSearchTerm = query.get('q') || '';
    if (urlSearchTerm) {
      setSearch(urlSearchTerm);
      setSearchInput(urlSearchTerm);
      setSelectedStatus([])
      doSearch(urlSearchTerm.trim());
    } else {
      setSearch('');
      setSearchInput('');
      fetchDefaultJobs([]);
    }
  }, [location.search, fetchDefaultJobs, doSearch]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    const searchedValue = searchInput.trim()
    setSearchInput(value);
    if(value.trim() == '' && !loading && searchedValue != ''){
      handleClearSearch()
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const trimmed = searchInput.trim();
      setSelectedStatus([])
      setSearch(trimmed);
      setPage(0);
      setHasMore(true);
      setError(null);
      doSearch(trimmed);
    }
  };

  const handleClearSearch = () => {
    setSearch('');
    setSearchInput('');
    setPage(0);
    setHasMore(true);
    setError(null);
    setSearchTotalCount(null);
    fetchDefaultJobs(selectedStatus);
  };

  const toggleStatus = (status) => {
    setSearchInput('')
    let newSelectedStatus;
    if (selectedStatus.includes(status)) {
      newSelectedStatus = selectedStatus.filter(s => s !== status);
    } else {
      newSelectedStatus = [...selectedStatus, status];
    }
    setSelectedStatus(newSelectedStatus);
    setPage(0);
    setHasMore(false);
    setError(null);
    setSearchTotalCount(null);
    fetchDefaultJobs(newSelectedStatus);
  };

  const selectAllStatuses = () => {
    setSelectedStatus([]);
    setPage(0);
    setHasMore(false);
    setError(null);
    setSearchTotalCount(null);
    if (searchInput) {
      doSearch(searchInput)
    } else {
      fetchDefaultJobs([]);
    }
  };

  const isStatusSelected = (status) => {
    return selectedStatus.includes(status);
  };

  const isAllSelected = () => {
    return selectedStatus.length === 0;
  };

  const handleInputFocus = () => {
    if (jobsListContainerRef.current) {
      jobsListContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleScroll = useCallback(() => {
    if (!search || loading || isFetchingMore || !hasMore) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 200) {
      fetchMoreJobs();
    }
  }, [search, loading, isFetchingMore, hasMore]);

  const fetchMoreJobs = useCallback(async () => {
    if (!search || isFetchingMore || !hasMore) return;
    const nextPage = page + 1;
    await doSearch(search, nextPage, true);
  }, [search, isFetchingMore, hasMore, page, doSearch]);

  useEffect(() => {
    if (!search) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [search, handleScroll]);

  const filteredJobs = jobs;

  let displayCount = loading ? '...' : (search ? (searchTotalCount !== null ? searchTotalCount : jobs.length) : jobs.length);

  return (
    <section ref={jobsListContainerRef} style={{ height: "calc(100vh - 150px)" }} className="scroll-mt-24">
      <div className="flex justify-between items-center mb-6">

        <div className="flex items-center gap-3">
          <div className="bg-gray-100 p-2 rounded-full">
            <FiBriefcase className="h-4 w-4 text-emerald-600" />
          </div>
          <h1 className="text-lg font-bold profiler">Latest job openings</h1>
          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-1 py-1 rounded-full">
            {displayCount}
          </span>
        </div>

        <div className="flex items-center gap-4 flex-1 max-w-3xl">
          {/* Filter icon to the left of the search input */}
          <div className="flex items-center gap-2 w-full relative">
            {/* Dropdown for active statuses */}
            <div className="relative w-full">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onFocus={handleInputFocus}
                maxLength={250}
                placeholder="Looking for a Piping Engineer with 8 years of experience"
                className="w-full border-gray-200 border rounded-full py-1.5 pl-12 pr-10 text-sm"
              />
              {searchInput && (
                <button
                  type="button"
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-200 text-gray-333 h-6 w-6 flex items-center justify-center rounded-full hover:bg-gray-300 transition-colors"
                  onClick={handleClearSearch}
                  tabIndex={0}
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>
          {/* Filter button and dropdown are wrapped in a relative container for correct dropdown positioning */}
          <div className="relative">
            <button
              type="button"
              aria-label="Filter"
              className="flex items-center justify-center hover:bg-gray-200 rounded-full p-1 border border-gray-200 mr-2 relative"
              style={{ minWidth: 36, minHeight: 36 }}
              tabIndex={0}
              onClick={() => setShowStatusDropdown((prev) => !prev)}
            >
              <FilterIcon />
              {/* Show badge if specific statuses are selected (not all) */}
              {!isAllSelected() && selectedStatus.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                  {selectedStatus.length}
                </span>
              )}
            </button>
            {showStatusDropdown && (
              <div className="absolute z-20 left-0 top-12 bg-white border border-gray-200 rounded shadow-md min-w-[180px] py-2">
                {Array.isArray(ACTIVE_STATUSES) && ACTIVE_STATUSES.length > 0 ? (
                  <>
                    <button
                      key="all"
                      type="button"
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${isAllSelected() ? 'bg-gray-100 font-semibold mph-primary' : 'text-gray-666'
                        }`}
                      onClick={() => {
                        selectAllStatuses();
                      }}
                    >
                      <span>All</span>
                      {isAllSelected() && <FiCheck className="text-emerald-600" />}
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    {ACTIVE_STATUSES.map((status) => (
                      <button
                        key={status}
                        type="button"
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${isStatusSelected(status) ? 'bg-gray-100 font-semibold mph-primary' : 'text-gray-666'
                          }`}
                        onClick={() => { toggleStatus(status); }}
                      >
                        <span>{status}</span>
                        {isStatusSelected(status) && <FiCheck className="text-emerald-600" />}
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="px-4 py-2 text-gray-400 text-sm">No statuses</div>
                )}
              </div>
            )}
          </div>
          <Link
            to="/jobs"
            className="flex items-center gap-2 text-xs font-semibold text-gray-700 profiler-bg border border-gray-300 px-4 py-1.5 rounded-full hover:bg-gray-50 whitespace-nowrap"
          >
            View All
            <FiArrowRight />
          </Link>
        </div>
      </div>

      <div
        className="space-y-4"
        ref={scrollContainerRef}
        style={{
          maxHeight: 'calc(100vh - 13.3rem)',
          overflowY: 'auto',
          position: 'relative'
        }}
      >
        {loading && (
          <div className="h-full justify-center flex flex-col">
            <Loader message="Loading jobs" />
          </div>
        )}
        {error && !loading && (
          <div className="text-gray-666 items-center flex text-sm mt-8 justify-center">{error}</div>
        )}
        {!loading && !error && filteredJobs.length === 0 && (
          <div className="text-gray-666 items-center flex text-sm mt-8 justify-center">No jobs found.</div>
        )}
        {!loading && !error && filteredJobs.map((job, index) => (
          <JobItem key={`${job.id}-${index}`} job={job} jobList={jobs} urlParam='dashboard' searchTerm={search} />
        ))}
        {/* Lazy loading loader */}
        {hasMore && !loading && isFetchingMore && (
          <div className="flex justify-center py-2 mt-20">
            <Loader message="Loading more jobs" />
          </div>
        )}
        {/* Show "No more jobs" if at end of search results */}
        {!loading && !isFetchingMore && search && !hasMore && filteredJobs.length > 0 && (
          <div className="text-gray-400 text-xs text-center py-2">No more jobs found.</div>
        )}
      </div>
    </section>
  );
};

export default JobsList;